import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Calendar, Clock, RefreshCw, Loader2, AlertCircle,
  CheckCircle, XCircle, Send, Search, X, Printer,
} from "lucide-react";
import {
  useGetMyExamCallApplicationsQuery,
  useSubmitExamCallApplicationMutation,
} from "../../api/examCallsApi";
import {
  printResultLetter, isInterview, resultLabel, isPositiveResult,
} from "../../utils/resultLetter";

// True when this application has a RELEASED result to print — an interview's
// recommendation letter, or a course exam's statement of result. Publication is
// the only gate: every exam produces a printable document now, so restricting
// this to MRI/PCI left TPC candidates with a result they could not print.
function canPrintResult(app: any): boolean {
  return isResultPublished(app);
}

/** What the printout is called, so the button names the document it opens. */
function resultDocumentLabel(app: any): string {
  // The exam's own flag decides, falling back to the code — the same test
  // printResultLetter uses, so the button cannot name one document and open
  // the other.
  return isInterview(app.exam ?? app.call?.exam) ? "Result Letter" : "Statement of Result";
}

/**
 * The candidate is done with this application — it has been marked.
 *
 * Either the status says so, or a result has come back. Both are checked
 * because the grading paths flip the status at different points, and either
 * signal means there is nothing left for the candidate to do.
 */
function isGraded(app: any): boolean {
  return !!app.result || ["graded", "completed"].includes(app.status);
}

/**
 * Whether the candidate is allowed to SEE the mark.
 *
 * Marked and released are two different things. A result exists from the moment
 * an examiner records it, but it is not the candidate's to read until it has
 * been published — so until `published_at` is set the application reads simply
 * as "Graded", and no score or outcome is shown anywhere on this page.
 */
function isResultPublished(app: any): boolean {
  return !!app.result?.published_at;
}

/** Has this application's call closed? */
function isCallClosed(app: any): boolean {
  const closes = app.call?.closes_at;
  return !!closes && new Date(closes) < new Date();
}

/**
 * Has the fee been settled?
 *
 * Read off the status, not `paid_at` — a recovery path on the server can move
 * a stuck application to `paid` from the payment record without stamping the
 * date. `draft` and `pending_payment` are the only two states before the money
 * lands. Mirrors ExamApplication::feeSettled().
 */
function feeSettled(app: any): boolean {
  return !!app.paid_at || !["draft", "pending_payment"].includes(app.status);
}

/**
 * Does the call's closing date still stop this candidate?
 *
 * Only while they have not paid. The window is there to stop NEW entrants —
 * once the fee is settled the candidate has bought their place in the sitting
 * and is owed the chance to finish the paperwork, deadline or no deadline.
 * This is also what makes a RETURN work after the closing date: a returned
 * application comes back to `paid`, and the secretariat asking for a
 * correction is worth nothing if the screen then refuses to take one.
 */
function windowBlocks(app: any): boolean {
  return isCallClosed(app) && !feeSettled(app);
}

/**
 * Whether the candidate still has something to do here.
 *
 * Marked is the end of it. So is a deadline they never paid before — but not a
 * deadline they paid before.
 */
function canAct(app: any): boolean {
  if (windowBlocks(app) || isGraded(app)) return false;

  return app.status === "draft"
    || ["pending_payment", "paid", "submitted", "under_review", "with_committee", "approved"].includes(app.status);
}

// The diet that carries the result-letter settings (direct or via the call).
function letterDiet(app: any): any {
  return app.diet ?? app.call?.diet ?? null;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return String(iso); }
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso); }
}

function safeMsg(err: any) {
  return err?.data?.message || err?.data?.error || err?.message || "Something went wrong";
}

type StatusKey =
  | "draft"
  | "pending_payment"
  | "paid"
  | "under_review"
  | "with_committee"
  | "approved"
  | "submitted"
  | "rejected"
  | "graded"
  | "completed"
  | "published";

const STATUS_MAP: Record<StatusKey, { cls: string; label: string; icon: React.ReactNode }> = {
  draft:           { cls: "bg-gray-100 text-gray-600",    label: "Draft",            icon: <FileText className="w-3 h-3" /> },
  pending_payment: { cls: "bg-amber-100 text-amber-700",  label: "Awaiting payment", icon: <Clock className="w-3 h-3" /> },
  paid:            { cls: "bg-blue-100 text-blue-700",    label: "Paid",             icon: <CheckCircle className="w-3 h-3" /> },
  under_review:    { cls: "bg-sky-100 text-sky-700",      label: "Under review",     icon: <Clock className="w-3 h-3" /> },
  with_committee:  { cls: "bg-indigo-100 text-indigo-700", label: "With committee",   icon: <Clock className="w-3 h-3" /> },
  approved:        { cls: "bg-green-100 text-green-700",  label: "Approved",         icon: <CheckCircle className="w-3 h-3" /> },
  submitted:       { cls: "bg-indigo-100 text-indigo-700", label: "Submitted",       icon: <Send className="w-3 h-3" /> },
  rejected:        { cls: "bg-red-100 text-red-700",      label: "Rejected",         icon: <XCircle className="w-3 h-3" /> },
  graded:          { cls: "bg-violet-100 text-violet-700", label: "Graded",          icon: <CheckCircle className="w-3 h-3" /> },
  completed:       { cls: "bg-teal-100 text-teal-700",    label: "Completed",        icon: <CheckCircle className="w-3 h-3" /> },
  // Not a database status — the state a released result puts the application in.
  published:       { cls: "bg-emerald-100 text-emerald-700", label: "Published",       icon: <CheckCircle className="w-3 h-3" /> },
};

/**
 * What the status column says.
 *
 * The column reports where the application stands, not what the mark was:
 * "Graded" once it has been marked, "Published" once the result has been
 * released. The mark itself belongs to the result panel and the result letter.
 */
function displayStatus(app: any): string {
  return isResultPublished(app) ? "published" : String(app.status ?? "draft");
}

function StatusBadge({ status }: { status?: string }) {
  const key = (status ?? "draft") as StatusKey;
  const cfg = STATUS_MAP[key] ?? STATUS_MAP.draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${cfg.cls}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Published result                                                     */
/* ------------------------------------------------------------------ */
/**
 * The candidate's released result, in full.
 *
 * Currently not rendered — the applications list deliberately shows status
 * only. Kept because hiding the result is a presentation decision that may
 * be reversed, not a removal of the feature.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ResultPanel({ result }: { result: any }) {
  const positive = isPositiveResult(result?.overall_status);

  return (
    <div
      className={`px-3 py-2 rounded-lg border text-xs ${
        positive
          ? "bg-green-50 border-green-100 text-green-800"
          : "bg-rose-50 border-rose-100 text-rose-800"
      }`}
    >
      <p className="font-semibold flex items-center gap-1.5">
        {positive ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
        Result: {resultLabel(result?.overall_status)}
      </p>

      {result?.papers_sat ? (
        <p className="mt-0.5">
          {result.papers_passed ?? 0} of {result.papers_sat} paper
          {result.papers_sat === 1 ? "" : "s"} passed
        </p>
      ) : null}

      {result?.remarks ? <p className="mt-0.5 italic">{result.remarks}</p> : null}

      {result?.published_at ? (
        <p className="mt-1 opacity-70">Released {fmtDate(result.published_at)}</p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Submit Confirm Modal                                                 */
/* ------------------------------------------------------------------ */
function SubmitConfirmModal({
  app, onConfirm, onClose, loading, error,
}: {
  app: any; onConfirm: () => void; onClose: () => void; loading: boolean; error?: string | null;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Submit Application</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm">
            <p className="font-semibold text-gray-900">{app.call?.exam?.title ?? "Exam"}</p>
            <p className="text-xs text-gray-500 mt-0.5">{app.call?.diet?.title ?? "—"}</p>
          </div>

          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-xs text-amber-800 space-y-1">
            <p className="font-semibold">Please note</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Submissions are final and cannot be reversed.</li>
              <li>Ensure you meet all eligibility requirements before submitting.</li>
              <li>The exam call closes on <strong>{fmtDate(app.call?.closes_at)}</strong>.</li>
            </ul>
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2.5 border rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            <Send className="w-4 h-4" />
            Submit Application
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Application Card (mobile)                                           */
/* ------------------------------------------------------------------ */
function AppCard({ app, onSubmit }: { app: any; onSubmit: (app: any) => void }) {
  const isDraft = app.status === "draft";
  // Kept with the hidden result panel further down — uncomment both together.
  // const published = isResultPublished(app);
  const callClosed = isCallClosed(app);
  const excludedCodes = ["MRI", "PCI"];
  /**
   * A card follows APPROVAL, not submission: it calls a candidate to the hall,
   * and until the Membership Committee has approved the application nobody has
   * agreed they may sit.
   *
   * It does NOT depend on the application window. That window closing is what
   * makes the exam imminent — it is exactly when a candidate goes looking for
   * their card — so tying the card to an open call hid it at the only moment
   * it was wanted, and left an approved candidate reading "the window has
   * closed" with nothing to press.
   */
  const canPrintCard =
    ["approved", "graded", "completed"].includes(app.status) &&
    !excludedCodes.includes((app.call?.exam?.code ?? app.exam?.code ?? "").toUpperCase());
  // Paid but never submitted: they owe us the rest of the application.
  const isUnfinished = app.status === "paid";
  // Open call, not yet marked, still theirs to finish — otherwise read-only.
  const actionable = canAct(app);
  const formMissing: string[] = Array.isArray(app.form_missing) ? app.form_missing : [];
  const sessionId = app.call?.diet?.session?.id ?? app.diet?.session?.id;

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <div className={`h-1 ${isDraft ? "bg-amber-400" : "bg-indigo-400"}`} />
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-gray-900 text-sm">{app.call?.exam?.title ?? app.exam?.title ?? "Exam"}</p>
            <p className="text-xs text-gray-500">{app.call?.diet?.title ?? app.diet?.title ?? "—"}</p>
          </div>
          {/* Until the result is released this reads "Graded"; once it is, the
              result panel below is the outcome and the badge adds nothing. */}
          <StatusBadge status={displayStatus(app)} />
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Applied {fmtDate(app.created_at)}
          </div>
          {app.submitted_at && (
            <div className="flex items-center gap-1">
              <Send className="w-3 h-3 text-indigo-400" /> Submitted {fmtDate(app.submitted_at)}
            </div>
          )}
          {app.center?.name && (
            <div className="col-span-2 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {app.center.name}
            </div>
          )}
        </div>

        {isDraft && actionable && (
          <Link
            to={`/exams/applications/${app.id}/finalize`}
            className="w-full py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
          >
            <Send className="w-3.5 h-3.5" /> Complete payment
          </Link>
        )}

        {/* The window has shut — but say so only where it actually STOPPED
            them. It is not a refusal to someone who paid before the deadline
            (they can still finish), nor to an application that was approved,
            graded or completed, where the window closing is just the normal
            course of events. Announcing it above a row of live buttons reads
            as a refusal that is not being made. */}
        {windowBlocks(app) && (
          <div className="px-3 py-2 bg-gray-50 border rounded-lg text-xs text-gray-600 text-center">
            The application window has closed. This application was never paid
            for and can no longer be submitted.
          </div>
        )}

        {/* Paid, the deadline has passed, and it is still theirs to finish.
            Worth saying out loud — the closing date is on the screen above and
            would otherwise read as the end of it. */}
        {callClosed && isUnfinished && (
          <div className="px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800 text-center">
            The application window has closed, but your fee is paid — you can
            still complete and submit this application.
          </div>
        )}

        {/* Marked and released — the outcome.
            Hidden for now: this list reports where an application stands, not
            what the mark was. Restore by uncommenting the line below.
        {published && <ResultPanel result={app.result} />} */}

        {/* Paid but not submitted — say plainly what is left to do. */}
        {isUnfinished && actionable && (
          <div className="px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-800">
            <p className="font-semibold">Your application is not submitted yet.</p>
            <p className="mt-0.5">
              {app.requires_application_form
                ? "Payment is confirmed. Complete your application form and send it for review."
                : "Payment is confirmed. Choose your centre and papers to submit it."}
            </p>
            {formMissing.length > 0 && (
              <ul className="mt-1.5 list-disc list-inside space-y-0.5">
                {formMissing.slice(0, 4).map((m) => (
                  <li key={m}>{m}</li>
                ))}
                {formMissing.length > 4 && <li>…and {formMissing.length - 4} more</li>}
              </ul>
            )}
          </div>
        )}

        {!isDraft && (
          <div className="flex flex-wrap gap-2">
            {/* The application itself: what was applied for, where, when it was
                submitted, the result once released — and the card and timetable
                where they exist. Always available, because reading your own
                application is not an action on it. */}
            <Link
              to={`/exams/applications/${app.id}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FileText className="w-3.5 h-3.5" /> Details
            </Link>

            {/* The action button exists only while the call is open and the
                application is still the candidate's to finish. */}
            {actionable && (
              <Link
                to={`/exams/applications/${app.id}/finalize`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors"
              >
                {app.status === "pending_payment"
                  ? "Complete payment"
                  : ["under_review", "with_committee", "approved"].includes(app.status)
                    ? "View application"
                    : app.status === "submitted"
                      ? "Manage application"
                      : "Finish application"}
              </Link>
            )}
            {canPrintResult(app) && (
              <button
                onClick={() => printResultLetter(app, letterDiet(app))}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-colors"
                title={`View and print your ${resultDocumentLabel(app).toLowerCase()}`}
              >
                <Printer className="w-3 h-3" /> {resultDocumentLabel(app)}
              </button>
            )}
            {canPrintCard && (
              <Link
                to={`/exams/applications/${app.id}/card`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-colors"
              >
                <Printer className="w-3 h-3" /> Admission Card
              </Link>
            )}
            {sessionId && (
              <Link
                to={`/exams/sessions/${sessionId}/timetable`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-colors"
              >
                <Printer className="w-3 h-3" /> Timetable
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Desktop Table                                                        */
/* ------------------------------------------------------------------ */
function DesktopTable({
  filtered, apps, onSubmit,
}: {
  filtered: any[]; apps: any[]; onSubmit: (app: any) => void;
}) {
  if (filtered.length === 0) {
    return (
      <div className="hidden md:block py-16 text-center bg-white border rounded-xl shadow-sm space-y-2">
        <FileText className="w-10 h-10 mx-auto text-gray-200" />
        <p className="text-sm text-gray-400">
          {apps.length === 0 ? "No applications yet." : "No applications match your filters."}
        </p>
        {apps.length === 0 && (
          <Link
            to="/exams/calls"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium"
          >
            <Calendar className="w-4 h-4" /> View Open Calls
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="hidden md:block bg-white border rounded-xl shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
          <tr>
            <th className="text-left font-semibold px-4 py-3">Exam / Diet</th>
            <th className="text-left font-semibold px-4 py-3">Applied</th>
            <th className="text-left font-semibold px-4 py-3">Submitted</th>
            <th className="text-left font-semibold px-4 py-3">Center</th>
            <th className="text-left font-semibold px-4 py-3">Status</th>
            <th className="text-right font-semibold px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {filtered.map((app: any) => {
            const isDraft = app.status === "draft";
            const excludedCodes = ["MRI", "PCI"];
            // Same rule as the card view: approval is the gate, and a closed
            // application window is not — see the note there.
            const canPrintCard =
              ["approved", "graded", "completed"].includes(app.status) &&
              !excludedCodes.includes((app.call?.exam?.code ?? app.exam?.code ?? "").toUpperCase());
            const sessionId = app.call?.diet?.session?.id ?? app.diet?.session?.id;
            // Nothing left to finish once it has been marked.
            const actionable = canAct(app);

            return (
              <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-semibold text-gray-900">{app.call?.exam?.title ?? app.exam?.title ?? "Exam"}</p>
                  <p className="text-xs text-gray-500">{app.call?.diet?.title ?? app.diet?.title ?? "—"}</p>
                </td>
                <td className="px-4 py-3 text-gray-600">{fmtDate(app.created_at)}</td>
                <td className="px-4 py-3 text-gray-600">{app.submitted_at ? fmtDate(app.submitted_at) : "—"}</td>
                <td className="px-4 py-3 text-gray-600">{app.center?.name ?? "—"}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={displayStatus(app)} />
                  {app.status === "paid" && (
                    <div className="text-[10px] text-amber-700 mt-1">Not submitted</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {/* Reading your own application is not an action on it, so
                        it is offered whatever the status and whether or not the
                        call has closed — the same as in the card view. */}
                    {!isDraft && (
                      <Link
                        to={`/exams/applications/${app.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <FileText className="w-3.5 h-3.5" /> Details
                      </Link>
                    )}
                    {isDraft && actionable && (
                      <Link
                        to={`/exams/applications/${app.id}/finalize`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors"
                      >
                        <Send className="w-3.5 h-3.5" /> Complete payment
                      </Link>
                    )}
                    {/* Only where the closing actually stopped them — see the
                        note in the card view. A candidate who paid before the
                        deadline is not stopped by it, so this must not sit
                        beside their still-live "Finish application" button. */}
                    {windowBlocks(app) && (
                      <span className="text-xs text-gray-400">Window closed</span>
                    )}
                    {!isDraft && actionable && (
                      <Link
                        to={`/exams/applications/${app.id}/finalize`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors"
                      >
                        {app.status === "pending_payment"
                          ? "Complete payment"
                          : ["under_review", "with_committee", "approved"].includes(app.status)
                            ? "View application"
                            : app.status === "submitted"
                              ? "Manage application"
                              : "Finish application"}
                      </Link>
                    )}
                    {!isDraft && canPrintResult(app) && (
                      <button
                        onClick={() => printResultLetter(app, letterDiet(app))}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-colors"
                        title={`View and print your ${resultDocumentLabel(app).toLowerCase()}`}
                      >
                        <Printer className="w-3 h-3" /> View Result
                      </button>
                    )}
                    {!isDraft && canPrintCard && (
                      <Link
                        to={`/exams/applications/${app.id}/card`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-colors"
                      >
                        <Printer className="w-3 h-3" /> Card
                      </Link>
                    )}
                    {!isDraft && sessionId && (
                      <Link
                        to={`/exams/sessions/${sessionId}/timetable`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-colors"
                      >
                        <Printer className="w-3 h-3" /> Timetable
                      </Link>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Page                                                            */
/* ------------------------------------------------------------------ */
export default function MyExamCallApplicationsPage() {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [submitTarget, setSubmitTarget] = useState<any | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);

  const { data: apps = [], isLoading, isFetching, error, refetch } = useGetMyExamCallApplicationsQuery();
  const [submitMutation, { isLoading: submitting }] = useSubmitExamCallApplicationMutation();

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return apps.filter((a: any) => {
      const blob = `${a.call?.exam?.title ?? ""} ${a.call?.diet?.title ?? ""}`.toLowerCase();
      const matchQ = !s || blob.includes(s);
      const matchS = statusFilter === "all" || a.status === statusFilter;
      return matchQ && matchS;
    });
  }, [apps, q, statusFilter]);

  const stats = useMemo(() => ({
    total: apps.length,
    draft: apps.filter((a: any) => a.status === "draft").length,
    submitted: apps.filter((a: any) => ["submitted", "paid"].includes(a.status)).length,
    approved: apps.filter((a: any) => a.status === "approved").length,
  }), [apps]);

  const handleSubmit = async () => {
    if (!submitTarget) return;
    setSubmitError(null);
    try {
      await submitMutation(submitTarget.id).unwrap();
      setSubmitTarget(null);
      setSuccessBanner(`Application for ${submitTarget.call?.exam?.title ?? "exam"} submitted successfully.`);
    } catch (err: any) {
      // The server refuses with a `missing` list when the application is short
      // — documents, CPD, a centre. Showing only "not complete yet" would leave
      // the candidate with nothing to act on and nowhere to go.
      const missing: string[] = Array.isArray(err?.data?.missing) ? err.data.missing : [];
      setSubmitError([safeMsg(err), ...missing].join(" "));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">

      {/* Header */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">My Exam Call Applications</h1>
              <p className="text-sm text-gray-500">Track and manage your exam call applications</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <Link
              to="/exams/calls"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm font-medium transition-colors"
            >
              <Calendar className="w-4 h-4" /> View Open Calls
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by exam or diet…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="paid">Paid</option>
            <option value="under_review">Under review</option>
            <option value="with_committee">With committee</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: stats.total, cls: "bg-gray-100 text-gray-600" },
          { label: "Draft", value: stats.draft, cls: "bg-amber-100 text-amber-600" },
          { label: "Submitted", value: stats.submitted, cls: "bg-indigo-100 text-indigo-600" },
          { label: "Approved", value: stats.approved, cls: "bg-green-100 text-green-600" },
        ].map(s => (
          <div key={s.label} className="bg-white border rounded-xl p-4 shadow-sm flex items-center gap-3">
            <div className={`p-2 rounded-lg ${s.cls}`}>
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">{s.label}</p>
              <p className="text-2xl font-bold text-gray-900 leading-tight">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Success banner */}
      {successBanner && (
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
          <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold mb-0.5">Submitted!</p>
            <p>{successBanner}</p>
          </div>
          <button onClick={() => setSuccessBanner(null)}><X className="w-4 h-4 text-green-600" /></button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="py-20 flex flex-col items-center gap-2 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-sm">Loading applications…</p>
        </div>
      ) : error ? (
        <div className="py-16 text-center bg-white border rounded-xl shadow-sm space-y-3">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
          <p className="text-sm text-red-500">Failed to load applications.</p>
          <button onClick={() => refetch()} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">Retry</button>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.length === 0 ? (
              <div className="py-16 text-center bg-white border rounded-xl shadow-sm space-y-2">
                <FileText className="w-10 h-10 mx-auto text-gray-200" />
                <p className="text-sm text-gray-400">
                  {apps.length === 0 ? "No applications yet." : "No applications match your filters."}
                </p>
                {apps.length === 0 && (
                  <Link
                    to="/exams/calls"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium"
                  >
                    <Calendar className="w-4 h-4" /> View Open Calls
                  </Link>
                )}
              </div>
            ) : (
              filtered.map((app: any) => (
                <AppCard key={app.id} app={app} onSubmit={(a) => { setSubmitTarget(a); setSubmitError(null); }} />
              ))
            )}
          </div>

          {/* Desktop table */}
          <DesktopTable
            filtered={filtered}
            apps={apps}
            onSubmit={(app) => { setSubmitTarget(app); setSubmitError(null); }}
          />
        </>
      )}

      {/* Draft reminder */}
      {!isLoading && stats.draft > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-0.5">
              You have {stats.draft} draft application{stats.draft !== 1 ? "s" : ""}
            </p>
            <p className="text-xs text-amber-700">
              Draft applications have not been submitted. You must submit before the exam call closes, or your application will be lost.
            </p>
          </div>
        </div>
      )}

      {/* Submit modal */}
      {submitTarget && (
        <SubmitConfirmModal
          app={submitTarget}
          onConfirm={handleSubmit}
          onClose={() => { setSubmitTarget(null); setSubmitError(null); }}
          loading={submitting}
          error={submitError}
        />
      )}
    </div>
  );
}